'use client';

import type { MouseEvent } from 'react';

import { useReducedMotion } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { MARKETING_ROUTES } from '@/common/constants';
import { DemoLink } from '@/components/demo-link';
import { AccentedTitle } from '@/components/section';
import { FINAL_CTA_COPY } from '@/lib/content';

export function FinalCtaSection() {
  const pathname = usePathname();
  const prefersReducedMotion = useReducedMotion();

  function handleScanClick(e: MouseEvent<HTMLAnchorElement>) {
    if (pathname !== '/') return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }

  return (
    <section id="book" className="scroll-mt-20">
      <div className="invert-surface">
        <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-6 px-8 py-28 text-center">
          {/* Not italic as a whole: in the comp only the closing clause is, which is what makes it
              read as the turn rather than as one long quote. */}
          <h2 className="max-w-[820px] text-[2rem] leading-[1.35] text-balance sm:text-[2.5rem]">
            <AccentedTitle text={FINAL_CTA_COPY.title} accent={FINAL_CTA_COPY.titleAccent} />
          </h2>
          <p className="max-w-[560px] text-lg leading-relaxed text-muted-foreground">
            {FINAL_CTA_COPY.body}
          </p>
          {/* Scan leads and demo follows, the same order of emphasis as the hero. This band is the
              one place both are full-size buttons: there is no scan field down here to carry the
              weight, so demoting the demo to a text link would leave the section with one control. */}
          <div className="mt-3 flex flex-col gap-3.5 sm:flex-row">
            <Link
              href={MARKETING_ROUTES.home}
              onClick={handleScanClick}
              className="inline-flex items-center justify-center rounded-[10px] bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground hover:opacity-85"
            >
              {FINAL_CTA_COPY.scan}
            </Link>
            <DemoLink className="inline-flex items-center justify-center rounded-[10px] border border-primary/35 px-7 py-3.5 text-base font-semibold hover:border-primary">
              {FINAL_CTA_COPY.demo}
            </DemoLink>
          </div>
        </div>
      </div>
    </section>
  );
}
